<script setup lang="ts">
import { formatCurrency } from "@/composables/formatCurrency";
import { PaymentFrequency } from "@/stores/pensionOptions";
import { captureException } from "@sentry/vue";
import { computed } from "vue";

const props = defineProps<{
    totalAnnualPension: number;
    frequency: PaymentFrequency;
}>();

const divisor = computed(() => {
    switch (props.frequency) {
        case PaymentFrequency.Monthly:
            return 12;
        case PaymentFrequency.Quarterly:
            return 4;
        case PaymentFrequency.HalfYearly:
            return 2;
        case PaymentFrequency.Yearly:
            return 1;
        default:
            captureException(new Error(`Unknown payment frequency: ${props.frequency}`));
            return 1;
    }
});

const formattedAmount = computed(() => {
    const amount = props.totalAnnualPension / divisor.value;
    return formatCurrency(amount, 2).split(".");
});

const frequencyText = computed(() => {
    switch (props.frequency) {
        case PaymentFrequency.Monthly:
            return "per month";
        case PaymentFrequency.Quarterly:
            return "per quarter";
        case PaymentFrequency.HalfYearly:
            return "per half year";
        case PaymentFrequency.Yearly:
            return "per year";
        default:
            captureException(new Error(`Unknown payment frequency: ${props.frequency}`));
            return "";
    }
});
</script>

<template>
    <h2 class="font-weight-bold line-height result-amount">
        {{ formattedAmount[0] }}
        <span class="pence">.{{ formattedAmount[1] }}</span>
        <small class="result-income-frequency">{{ frequencyText }}</small>
    </h2>
</template>

<style scoped>
.result-amount {
    margin: 0;
    font-size: 1.8rem;
    line-height: 1.2;
    text-align: center;
}

.result-income-frequency {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.9rem;
}

@media (max-width: 768px) {
    .result-amount {
        font-size: 1.95rem;
        line-height: 1.15;
    }
}
</style>
